import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import SingleDropDown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import LockedIcon from 'ext-ui/svg/icon/LockedIcon'
import React, {
  ChangeEvent,
  FormEvent,
  ReactElement,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectIssuerIdFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  amexCardTypes,
  amexIssuerCodes,
} from 'src/app/RegisterAccount/RegisterAccount'
import { ReviewEnrollCardProps } from 'src/app/ReviewEnrollCard/ReviewEnrollCard'
import { AddressFormFields } from 'src/component-form/AddressForm/AddressForm'
import RadioItems from 'src/components/RadioItems/RadioItems'
import { Issuers, IssuerSchemaFilterFields } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { IssuerIntegratorListModel } from 'src/models/issuerIntegrator.models'
import {
  countriesQueries,
  toCountryListModelMap,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { issuerIntegratorQueries } from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import style from './EditCardDetailsModal.style'

const ANALYTICS_EVENT = 'edit card details modal'

type SubmissionData = ReviewEnrollCardProps['submissionData']

export type EditCardDetailsModalProps = BaseModalProps & {
  submissionData: SubmissionData
  onSubmit: (updated: SubmissionData) => void
}

type EditFormFields = AddressFormFields & {
  expiration: string
  cvc: string
}

// Replace every digit that has at least 4 more digits after it. Spaces
// pass through, so '4111 1111 1111 1234' → '**** **** **** 1234'.
const maskAllButLast4 = (cardNumber: string): string =>
  cardNumber.replace(/\d(?=.*\d{4})/g, '*')

const getPostalPlaceholderForCountry = (
  country: string,
  translate: (s: string) => string,
): string => {
  if (country === 'US') return '#####'
  if (country === 'CA') return '### ###'
  return translate('Enter Postal Code')
}

export default function EditCardDetailsModal(
  props: EditCardDetailsModalProps,
): ReactElement {
  const { className = '', isOpen, onClose, submissionData, onSubmit } = props

  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  // The issuer id comes from the BIN-range lookup; the detail query shares
  // the ['issuers', id] cache entry with the rest of the enroll flow.
  const issuerId = useSelector(selectIssuerIdFromState)
  const { data: selectedIssuer } = useQuery({
    ...issuerIntegratorQueries.detail(issuerId),
    select: (data) => new IssuerIntegratorListModel(data),
  })
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Legacy behavior rendered with empty options/map until the fetch
  // succeeded, so the `= []` / `= {}` defaults preserve that exactly.
  const { data: allCountryOptions = [] } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryOptions,
  })
  const { data: countriesMap = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })

  const isAmexIssuer = amexIssuerCodes.includes(selectedIssuer?.code as Issuers)

  const issuerCvcRequired =
    selectedIssuer?.schema?.some(
      (field) =>
        field.name === IssuerSchemaFilterFields.cvc && field.required === true,
    ) ?? false

  // Issuer-specific extras (everything except the card-number/expires/cvc
  // we already capture via cardData).
  const additionalIssuerFields = (selectedIssuer?.schema ?? []).filter(
    (field) =>
      !(
        [
          IssuerSchemaFilterFields.cardNumber,
          IssuerSchemaFilterFields.expires,
          IssuerSchemaFilterFields.cvc,
        ] as string[]
      ).includes(field.name),
  )

  const additionalIssuerFieldDefaults = additionalIssuerFields.reduce(
    (acc, field) => {
      acc[field.name] = submissionData.issuerData?.[field.name] ?? ''
      return acc
    },
    {} as Record<string, string>,
  )

  const initialFields: EditFormFields = {
    cardName: submissionData.addressData.cardName ?? '',
    cardholderName: submissionData.addressData.cardholderName ?? '',
    country: submissionData.addressData.country ?? 'US',
    address1: submissionData.addressData.address1 ?? '',
    address2: submissionData.addressData.address2 ?? '',
    city: submissionData.addressData.city ?? '',
    province: submissionData.addressData.province ?? '',
    postal: submissionData.addressData.postal ?? '',
    expiration: submissionData.cardData.expiration ?? '',
    cvc: submissionData.cardData.cvc ?? '',
    ...additionalIssuerFieldDefaults,
    ...(submissionData.addressData.cardType
      ? { cardType: submissionData.addressData.cardType }
      : {}),
  }

  const { fields, setFields, fieldsErrors, setFieldsErrors } =
    useFormFields<EditFormFields>(
      initialFields,
      (): FormFieldsValidation<EditFormFields> => ({
        cardName: [],
        cardholderName: [],
        country: [],
        address1: [],
        address2: [],
        city: [],
        province: [],
        postal: [],
        expiration: [],
        cvc: [],
      }),
    )

  // Amex card-type radio uses an explicit selected index so the radio group
  // reflects the current `cardType` value without re-deriving on every render.
  const initialAmexIndex = isAmexIssuer
    ? amexCardTypes.findIndex((ct) => ct.value === fields.cardType)
    : -1
  const [amexIndex, setAmexIndex] = useState<number>(initialAmexIndex)

  // Mount-only — same pattern as ReviewEnrollCard.
  useEffect(() => {
    analytics.track(ANALYTICS_EVENT, {
      action: 'viewed',
      card_eligibility: submissionData.binData.eligibility,
      issuer_code: selectedIssuer?.code,
    })
  }, [])

  const countryOptions = (
    selectedIssuer?.restrictions?.country !== undefined
      ? allCountryOptions.filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : allCountryOptions
  ).map((opt) => ({
    value: opt.value,
    label: opt.details.text,
  }))

  const handleClose = () => {
    analytics.track(ANALYTICS_EVENT, {
      action: 'dismissed',
      card_eligibility: submissionData.binData.eligibility,
      issuer_code: selectedIssuer?.code,
    })
    onClose()
  }

  const handleAmexChange = (index: number) => {
    setAmexIndex(index)
    setFields({
      ...fields,
      cardType: amexCardTypes[index]?.value,
    })
  }

  const handleFormChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget
    setFields({
      ...fields,
      [name]: value,
    })
  }

  const handleCountryChange = ({ value }: DropdownValue) => {
    setFields({
      ...fields,
      country: value,
      // Province options are country-specific — clear it so a value from the
      // previous country can't sneak through.
      province: '',
    })
  }

  const handleProvinceChange = ({ value }: DropdownValue) => {
    setFields({
      ...fields,
      province: value,
    })
  }

  const validate = (): boolean => {
    const errors: Partial<Record<keyof EditFormFields, string[]>> = {}

    const country = countriesMap[fields.country]
    if (country) {
      country.fields.forEach((countryField) => {
        const fieldName = countryField.name as keyof EditFormFields
        const valueToValidate = fields[fieldName]
        if (!countryField.optional && !valueToValidate) {
          errors[fieldName] = [translate('Must not be blank')]
          return
        }
        if (countryField.format && valueToValidate) {
          const regex = new RegExp(countryField.format)
          if (!regex.test(valueToValidate)) {
            errors[fieldName] = [translate('Invalid value')]
          }
        }
      })
    }

    // city is required by the server but flagged optional in the country
    // schema for some locales — match AddressForm's manual override.
    if (!fields.city) {
      errors.city = [translate('Must not be blank')]
    }

    if (!fields.cardName) {
      errors.cardName = [translate('Must not be blank')]
    }

    if (!fields.cardholderName) {
      errors.cardholderName = [translate('Must not be blank')]
    }

    if (issuerCvcRequired && !fields.cvc) {
      errors.cvc = [translate('Must not be blank')]
    }

    if (!fields.expiration) {
      errors.expiration = [translate('Must not be blank')]
    }

    if (isAmexIssuer && !fields.cardType) {
      errors.cardType = [translate('Must not be blank')]
    }

    additionalIssuerFields.forEach((extra) => {
      if (extra.required && !fields[extra.name]) {
        errors[extra.name as keyof EditFormFields] = [
          translate('Must not be blank'),
        ]
      }
    })

    setFieldsErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleContinue = () => {
    if (!validate()) return

    const issuerData: Record<string, string> = {
      ...(submissionData.issuerData ?? {}),
    }
    additionalIssuerFields.forEach((extra) => {
      issuerData[extra.name] = fields[extra.name] ?? ''
    })

    const fieldsChanged: string[] = []
    const original = submissionData.addressData
    ;(
      [
        'cardName',
        'cardholderName',
        'country',
        'address1',
        'address2',
        'city',
        'province',
        'postal',
        'cardType',
      ] as const
    ).forEach((key) => {
      if ((fields[key] ?? '') !== (original[key] ?? '')) {
        fieldsChanged.push(key)
      }
    })
    if (fields.expiration !== submissionData.cardData.expiration) {
      fieldsChanged.push('expiration')
    }
    if (fields.cvc !== (submissionData.cardData.cvc ?? '')) {
      fieldsChanged.push('cvc')
    }

    analytics.track(ANALYTICS_EVENT, {
      action: 'saved',
      card_eligibility: submissionData.binData.eligibility,
      issuer_code: selectedIssuer?.code,
      fields_changed: fieldsChanged,
    })

    onSubmit({
      binData: submissionData.binData,
      cardData: {
        ...submissionData.cardData,
        expiration: fields.expiration,
        ...(issuerCvcRequired ? { cvc: fields.cvc } : {}),
      },
      addressData: {
        ...submissionData.addressData,
        cardName: fields.cardName,
        cardholderName: fields.cardholderName,
        country: fields.country,
        address1: fields.address1,
        address2: fields.address2,
        city: fields.city,
        province: fields.province,
        postal: fields.postal,
        ...(fields.cardType ? { cardType: fields.cardType } : {}),
      },
      issuerData,
    })
  }

  const country = countriesMap[fields.country]

  return (
    <BaseModal
      className={cn('EditCardDetailsModal', className)}
      variant="interactive"
      isOpen={isOpen}
      onClose={handleClose}
      css={style}
      aria-label={translate('Edit Card Details')}
    >
      <p className="EditCardDetailsModal-header">
        {translate('Edit Card Details')}
      </p>
      <form
        className="EditCardDetailsModal-form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          handleContinue()
        }}
      >
        <TextInput
          name="cardNumber"
          testId="cardNumber"
          label={translate('Card Number')}
          value={maskAllButLast4(submissionData.cardData.number)}
          disabled
          iconRight={<LockedIcon aria-hidden />}
          onChange={() => undefined}
        />

        <div className="EditCardDetailsModal-row-pair">
          <TextInput
            name="expiration"
            testId="expiration"
            label={translate('Expiration')}
            value={fields.expiration}
            onChange={handleFormChange}
            errorText={fieldsErrors.expiration}
          />
          {issuerCvcRequired && (
            <TextInput
              name="cvc"
              testId="cvc"
              label={translate('CVC (if applicable)')}
              value={fields.cvc}
              onChange={handleFormChange}
              errorText={fieldsErrors.cvc}
            />
          )}
        </div>

        <TextInput
          name="cardholderName"
          testId="cardholderName"
          label={translate('Cardholder Name')}
          value={fields.cardholderName}
          onChange={handleFormChange}
          errorText={fieldsErrors.cardholderName}
        />

        <SingleDropDown
          data-testid="country"
          label={translate('Country')}
          options={countryOptions}
          selectedValue={countryOptions.find(
            (opt) => opt.value === fields.country,
          )}
          onSelect={handleCountryChange}
          placeholder={translate('Enter your country')}
          searchable
          autoComplete="country"
          errorText={fieldsErrors.country}
        />

        {country?.fields.map((countryField) => {
          const { options, label, name } = countryField
          if (options) {
            const selected = options.find((opt) => opt.code === fields.province)
            return (
              <SingleDropDown
                key={name}
                data-testid={name}
                label={translate(label)}
                options={options.map((opt) => ({
                  value: opt.code,
                  label: opt.label,
                }))}
                selectedValue={
                  selected
                    ? { label: selected.label, value: selected.code }
                    : undefined
                }
                onSelect={handleProvinceChange}
                searchable
                placeholder={translate('Select your ${label}', { label })}
                errorText={fieldsErrors[name as keyof EditFormFields]}
                autoComplete="address-level1"
              />
            )
          }
          return (
            <TextInput
              key={name}
              name={name}
              testId={name}
              label={translate(label)}
              value={fields[name as keyof EditFormFields] ?? ''}
              onChange={handleFormChange}
              placeholder={
                name === 'postal'
                  ? getPostalPlaceholderForCountry(fields.country, translate)
                  : translate('Enter ${label}', { label })
              }
              errorText={fieldsErrors[name as keyof EditFormFields]}
            />
          )
        })}

        {additionalIssuerFields.map((extra) => (
          <TextInput
            key={extra.name}
            name={extra.name}
            testId={extra.name}
            label={translate(extra.label)}
            value={fields[extra.name] ?? ''}
            onChange={handleFormChange}
            microcopy={extra.description}
            errorText={fieldsErrors[extra.name as keyof EditFormFields]}
          />
        ))}

        {isAmexIssuer && (
          <RadioItems
            items={amexCardTypes.map((cardType) => (
              <span
                key={cardType.value}
                data-testid={`radio-button-${cardType.value.replace(/_/g, '-')}`}
              >
                <p className="font-regular">{cardType.fieldText}</p>
              </span>
            ))}
            currentIndex={amexIndex}
            setIndex={handleAmexChange}
            horizontalAlign
            staticOnHover
            noBorder
            alignStart
            gap={'40px'}
            errors={fieldsErrors.cardType}
            ariaLabels={amexCardTypes.map((ct) =>
              typeof ct.fieldText === 'string'
                ? translate(ct.fieldText)
                : translate(ct.fieldText.props.source),
            )}
          />
        )}

        <TextInput
          name="cardName"
          testId="cardName"
          label={translate('Card Display Name')}
          value={fields.cardName}
          onChange={handleFormChange}
          microcopy={translate(
            "Your card's display name appears at the top of the card detail screen, as well as anywhere that refers to the card. You can update it at any time.",
          )}
          errorText={fieldsErrors.cardName}
        />

        <div className="EditCardDetailsModal-actions">
          <Button type="submit" data-testid="continue-button">
            {translate('Continue')}
          </Button>
        </div>
      </form>
    </BaseModal>
  )
}
